import { ReactElement, useMemo, useCallback } from "react";
import { TaskListFormProps } from "./TaskListForm.types";
import { FormSection, FormControl, FormTokenizedObjectInput } from "src/ui/components/Forms";
import { TextField, DatePicker, Checkbox, IDropdownOption, Dropdown } from "@fluentui/react";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import ITokenizedInputItem from "src/ui/components/Forms/ITokenizedInputItem";
import { Conditional } from "src/components/Conditional";

const localizationKeys = [
    "Namn",
    "Typ",
    "Installningar",
    "RECURRING",
    "QH_RESET_FREQUENCY",
    "Startdatum",
    "Slutdatum",
    "RiktadTill",
    "RiktadTillBeskrivning",
    "ALLOW_CUSTOMIZATION",
] as const;

function memoizeLocalizationKeys(keys: readonly string[]) {
    return keys.reduce((dict, key) => ({ ...dict, [key]: localize(key) }), {});
}

type RecurrenceOptions
    = "NEVER"
    | "DAILY"
    | "WEEKLY"
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMIANNUALLY"
    | "YEARLY";

function getRecurrenceOptions(): IDropdownOption<RecurrenceOptions>[] {
    return [
        {
            data: "NEVER",
            key: "NEVER",
            text: localize("Aldrig"),
        },
        {
            data: "DAILY",
            key: "DAILY",
            text: localize("DAILY"),
        },
        {
            data: "WEEKLY",
            key: "WEEKLY",
            text: localize("WEEKLY"),
        },
        {
            data: "MONTHLY",
            key: "MONTHLY",
            text: localize("MONTHLY"),
        },
        {
            data: "QUARTERLY",
            key: "QUARTERLY",
            text: localize("QUARTERLY"),
        },
        {
            data: "SEMIANNUALLY",
            key: "SEMIANNUALLY",
            text: localize("SEMIANNUALLY"),
        },
        {
            data: "YEARLY",
            key: "YEARLY",
            text: localize("YEARLY"),
        },
    ];
}

type TextFieldChangeHandler = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
) => void;

type CheckboxChangeHandler = (
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean
) => void;

const targetTypes = [
    SpintrTypes.UberType.User,
    SpintrTypes.UberType.Department,
    SpintrTypes.UberType.Office,
    SpintrTypes.UberType.Company,
    SpintrTypes.UberType.Role,
    SpintrTypes.UberType.TargetGroup,
] as const;


/**
 * Renders a Task List Form component based on the provided props.
 *
 * @param {TaskListFormProps} props - The properties to configure the Task List Form.
 * @returns {ReactElement} The rendered Task List Form component.
 */
function TaskListForm({
    model,
    onModelChanged,
}: TaskListFormProps): ReactElement {
    const l10n = useMemo(() => memoizeLocalizationKeys(localizationKeys), []);

    const onNameChanged = useCallback<TextFieldChangeHandler>(
        (_, newValue) => onModelChanged?.((model) => ({
                ...model,
                name: newValue ?? "",
        })),
        [onModelChanged]);

    const onAllowOverrideChanged = useCallback<CheckboxChangeHandler>(
        (_, checked) => onModelChanged?.((model) => ({
                ...model,
                allowOverride: checked ?? false,
        })),
        [onModelChanged]);

    const onClearEndDateClicked = useCallback(() => onModelChanged?.((model) => ({
        ...model,
        endDate: undefined,
    })), [onModelChanged]);

    const onTargetsChanged = useCallback((items: ITokenizedInputItem[]) =>
        onModelChanged?.((model) => ({
            ...model,
            targets: items.map((item) => ({
                ...item,
                id: item.key,
                name: item.name,
                type: item.type,
                image: item.imageUrl,
                info: item.subText,
            }))
        })), [onModelChanged]);

    const targets = useMemo<ITokenizedInputItem[]>(
        () => (model.targets || []).map((target) => ({
            ...target,
            key: target.id,
            subText: target.info,
            imageUrl: target.image,
        })),
        [model.targets]);

    const recurrenceOptions = useMemo(() => getRecurrenceOptions(), []);

    const onRecurrenceOptionChanged = useCallback((
        _: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption<RecurrenceOptions>,
    ) => onModelChanged?.((model) => {
        if (!option.data || option.data === "NEVER") {
            return {
                ...model,
                recurrencePattern: { type: SpintrTypes.RecurrenceType.None, interval: 1 },
            };
        }

        let recurrenceReferenceDate = model.recurrenceReferenceDate;
        let recurrencePattern: Spintr.RecurrencePattern;
        switch (option.data) {
        case "DAILY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Daily,
                interval: 1,
            };
            break;
        case "WEEKLY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Weekly,
                interval: 1,
                daysOfWeek: [1],
            };
            break;
        case "MONTHLY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Monthly,
                interval: 1,
                day: 1,
            };
            break;
        case "QUARTERLY":
            recurrenceReferenceDate = new Date(recurrenceReferenceDate.getFullYear(), recurrenceReferenceDate.getMonth() - (recurrenceReferenceDate.getMonth() % 3), 1);
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Monthly,
                interval: 3,
                day: 1,
            };
            break;
        case "SEMIANNUALLY":
            recurrenceReferenceDate = new Date(recurrenceReferenceDate.getFullYear(), recurrenceReferenceDate.getMonth() - (recurrenceReferenceDate.getMonth() % 6), 1);
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Monthly,
                interval: 6,
                day: 1,
            };
            break;
        case "YEARLY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Yearly,
                interval: 1,
                month: 1,
                day: 1,
            };
            break;
        default:
            return model;
        }

        return {
            ...model,
            recurrencePattern,
            recurrenceReferenceDate,
        };
    }), [onModelChanged]);

    const selectedRecurrenceOption = useMemo(() => {
        switch (model.recurrencePattern?.type) {
        case SpintrTypes.RecurrenceType.Daily:
            return recurrenceOptions.find((opt) => opt.data === "DAILY");
        case SpintrTypes.RecurrenceType.Weekly:
            return recurrenceOptions.find((opt) => opt.data === "WEEKLY");
        case SpintrTypes.RecurrenceType.Monthly:
            return recurrenceOptions.find((opt) => opt.data === "MONTHLY");
        default:
            return recurrenceOptions.find((opt) => opt.data === "NEVER");
        }
    }, [model.recurrencePattern, recurrenceOptions]);

    const onRecurringChanged = useCallback<CheckboxChangeHandler>(
        (_, checked) => onModelChanged?.((model) => ({
            ...model,
            recurrencePattern: !checked
                ? { type: SpintrTypes.RecurrenceType.None, interval: 1 }
                : { type: SpintrTypes.RecurrenceType.Daily, interval: 1 },
        })),
        [onModelChanged]);

    const onPublishedChanged = useCallback(
        (_: unknown, checked?: boolean) => onModelChanged?.((model) => ({
            ...model,
            contentStatus: !checked
                ? SpintrTypes.ContentStatus.Draft
                : SpintrTypes.ContentStatus.Published,
        })), [onModelChanged]);

    const isRecurring = !!model?.recurrencePattern?.type
                     && model.recurrencePattern.type !== SpintrTypes.RecurrenceType.None;

    return (
        <div className="TaskListForm">
            <form action="#"
                  method="POST">
                <FormSection>
                    <FormControl>
                        <TextField aria-required={true}
                                   className="textField"
                                   label={l10n["Namn"]}
                                   name="name"
                                   onChange={onNameChanged}
                                   required={true}
                                   value={model.name} />
                    </FormControl>
                </FormSection>

                <FormSection className="TaskListForm-dates">
                    <FormControl className="TaskListForm-startDate">
                        <DatePicker ariaLabel={l10n["Startdatum"]}
                                    firstDayOfWeek={1}
                                    isRequired={true}
                                    label={l10n["Startdatum"]}
                                    placeholder={l10n["Startdatum"]}
                                    value={model.startDate} />
                    </FormControl>
                    <FormControl className="TaskListForm-endDate">
                        <DatePicker ariaLabel={l10n["Slutdatum"]}
                                    className="TaskListForm-datePicker"
                                    firstDayOfWeek={1}
                                    isRequired={false}
                                    label={l10n["Slutdatum"]}
                                    placeholder={l10n["Slutdatum"]}
                                    value={model.endDate} />
                        <UnstyledButton className="clear-button"
                                        onClick={onClearEndDateClicked}>
                            <Visage2Icon color="dark-grey"
                                         icon="close-square"
                                         size="big" />
                        </UnstyledButton>
                    </FormControl>
                </FormSection>

                <FormSection title={l10n["Installningar"]}>
                    <FormControl>
                        <Checkbox label={l10n["ALLOW_CUSTOMIZATION"]}
                                  name="allowOverride"
                                  onChange={onAllowOverrideChanged}
                                  checked={model.allowOverride} />
                    </FormControl>

                    <FormControl>
                        <Checkbox label={l10n["RECURRING"]}
                                  name="recurring"
                                  onChange={onRecurringChanged}
                                  checked={isRecurring} />

                    </FormControl>

                    <Conditional condition={isRecurring}>
                        <FormControl>
                            <Dropdown label={l10n["QH_RESET_FREQUENCY"] + ":"}
                                      onChange={onRecurrenceOptionChanged}
                                      options={recurrenceOptions}
                                      selectedKey={selectedRecurrenceOption?.key} />
                        </FormControl>
                    </Conditional>

                    <FormControl label={localize("Status")}>
                        <Checkbox checked={model.contentStatus === SpintrTypes.ContentStatus.Published}
                                  label={localize("Publicerad")}
                                  name="contentStatus"
                                  onChange={onPublishedChanged} />
                    </FormControl>

                    <FormControl label={l10n["RiktadTill"]}>
                        <FormTokenizedObjectInput items={targets}
                                                  label={l10n["RiktadTillBeskrivning"]}
                                                  onChange={onTargetsChanged}
                                                  showAllWhenEmpty={true}
                                                  types={targetTypes} />
                    </FormControl>
                </FormSection>

            </form>
        </div>
    );
}

export default TaskListForm;

